import React from 'react';
import { styled } from '@mui/material';
import { PieArcDatum } from 'd3';
import { PIE_ANIMATION_CONFIG } from './pie.constants';
import { MIN_HIGHLIGHT_ANGLE, SELECTED_RADIUS_FACTOR, STROKE_WIDTH } from './PieSlice.utils';

export const pieSliceClassNames = {
  arc: 'Chart-PieArc',
  ring: 'Chart-PieRing',
} as const;

const SURFACE_COLOR = 'var(--color-surface-base-primary)';

export type SliceProps<Datum> = {
  arcPath: string;
  ringArc?: string | null;
  baseRadius: number;
  innerRadius: number;
  index: number;
  selected: boolean;
  color: string;
} & Omit<PieArcDatum<Datum>, 'data' | 'datum'>;

export const PieSlice = <Datum,>({
  startAngle,
  endAngle,
  arcPath,
  ringArc,
  selected,
  index,
  color,
  baseRadius,
  innerRadius,
}: SliceProps<Datum>) => {
  const id = React.useId();
  const outerR = selected ? baseRadius * (1 + SELECTED_RADIUS_FACTOR) : baseRadius;
  const angularSpan = endAngle - startAngle;
  const showStroke = angularSpan >= MIN_HIGHLIGHT_ANGLE;
  const gradientId = !showStroke ? `pie-edge-grad-${id}` : undefined;
  const gradientStrokeOpacity = gradientId ? Math.min(1, angularSpan / MIN_HIGHLIGHT_ANGLE) : undefined;

  return (
    <>
      {gradientId && <EdgeStrokeGradient id={gradientId} outerRadius={outerR} innerRadius={innerRadius} />}
      <StyledArcPath
        className={pieSliceClassNames.arc}
        data-index={index}
        d={arcPath}
        fill={color}
        stroke={showStroke ? SURFACE_COLOR : gradientId ? `url(#${gradientId})` : 'none'}
        strokeWidth={STROKE_WIDTH}
        strokeOpacity={gradientStrokeOpacity}
      />
      {ringArc && <StyledRingPath aria-hidden="true" className={pieSliceClassNames.ring} d={ringArc} fill={color} />}
    </>
  );
};

type EdgeStrokeGradientProps = { id: string; outerRadius: number; innerRadius: number };

const EdgeStrokeGradient = React.memo(({ id, outerRadius, innerRadius }: EdgeStrokeGradientProps) => {
  const isDonut = innerRadius > 0;
  const innerEdgeStart = innerRadius / outerRadius;
  const innerEdgeEnd = (innerRadius + STROKE_WIDTH) / outerRadius;
  const midpoint = (innerRadius + outerRadius) / 2 / outerRadius;
  const outerEdgeStart = (outerRadius - STROKE_WIDTH) / outerRadius;

  return (
    <radialGradient id={id} cx={0} cy={0} r={outerRadius} gradientUnits="userSpaceOnUse">
      {isDonut && <stop offset={innerEdgeStart} stopColor={SURFACE_COLOR} />}
      {isDonut ? (
        <stop offset={innerEdgeEnd} stopColor={SURFACE_COLOR} stopOpacity={0} />
      ) : (
        <>
          <stop offset={innerEdgeStart} stopColor={SURFACE_COLOR} />
          <stop offset={midpoint} stopColor={SURFACE_COLOR} stopOpacity={0} />
        </>
      )}
      <stop offset={outerEdgeStart} stopColor={SURFACE_COLOR} stopOpacity={0} />
      <stop offset={1} stopColor={SURFACE_COLOR} />
    </radialGradient>
  );
});

const StyledArcPath = styled('path')(({ theme }) => ({
  cursor: 'default',
  transformBox: 'fill-box',
  transformOrigin: 'center',
  transition: theme.transitions.create(['opacity', 'filter', 'transform', 'd'], PIE_ANIMATION_CONFIG),

  '[data-interactive] &': {
    cursor: 'pointer',
  },
  '[data-selected] &': {
    transform: 'scale(1.015)',
  },
  '[data-highlighted] &': {
    filter: 'brightness(1.05)',
  },
  'g[data-has-selection] g:not([data-selected]) &': {
    opacity: 0.15,
  },
}));

const StyledRingPath = styled('path')(({ theme }) => ({
  pointerEvents: 'none',
  opacity: 0,
  transition: theme.transitions.create(['opacity', 'd'], PIE_ANIMATION_CONFIG),

  '[data-highlighted] &': {
    opacity: 0.5,
  },
  'g[data-has-selection] [data-highlighted]:not([data-selected]) &': {
    opacity: 0.05,
  },
}));
